// The drum-machine view of a MIDI clip (lib/step-grid.ts): rows of pitches,
// columns of steps, bars ruled off and beats shaded in turn. The gestures
// are a drum machine's (and FL Studio's Channel Rack): tap a cell for a hit,
// tap it again to clear it; press an EMPTY cell and sweep to paint a run;
// the right button erases, a right sweep a run; ⇧-tap cycles a hit's
// velocity normal → accent → ghost. A lit cell moves as a note does in the
// piano roll: press it and drag — along for time, across rows for another
// drum — and it lands where the pointer lifts (a press that never leaves
// its cell is still a tap, and clears). A focused hit takes the roll's keys
// too: Delete clears it, ← → move it a step (⇧ a bar), ↑ ↓ a row. A sweep or
// a move is ONE edit — one undo step, one envelope. Rows run
// high to low, as a piano roll does; with a scale a melodic clip's rows are
// its degrees (the root marked, a stray note's row tinted) and "+" opens
// the next degree above or below.

import { useEffect, useMemo, useRef, useState } from "react";
import type { WireNote } from "@shared/midi/notes.js";
import { cn } from "@web-shared/lib/utils";
import { beatGroup, drumLabel, moveStep, neighbourPitch, nextVelocity, nudgeCell, paintSteps, pitchLabel, rowsFor, setStep, setStepVelocity, stepCount, stepOf, stepsOf, toneOf, type DrumNames, type RowScale, type StepCell } from "@web-shared/lib/step-grid";

interface Props {
  notes: readonly WireNote[];
  clipBeats: number;
  stepBeats: number;
  beatsPerBar: number;
  /** Show the kit's rows even when empty. */
  kit: boolean;
  /** The kit's own pad names, by pitch, where known — else General MIDI's. */
  drumNames?: DrumNames | undefined;
  editable: boolean;
  /** The scale the melodic rows follow (the roll's: the Set's, or the browser's aid). */
  scale?: (RowScale & { label: string }) | undefined;
  playheadBeats?: number | undefined;
  onChange: (next: WireNote[]) => void;
  onAudition?: ((note: WireNote) => void | (() => void)) | undefined;
}

/** A press that may become a sweep (from an empty cell, or the right
 * button) or a move (from a lit one): what it does, what it has crossed. */
interface Sweep {
  /** "paint" / "erase": every cell crossed is set; "move": the pressed hit
   * follows the pointer, `from` → `to`. */
  mode: "paint" | "erase" | "move";
  from: StepCell;
  to: StepCell;
  /** The notes as edited so far — drawn in place of the props' while the pointer is down. */
  notes: readonly WireNote[];
  crossed: Set<string>;
  pointerId: number;
}

const ACCENT = 120;
const GHOST = 64;

export function StepGrid({ notes, clipBeats, stepBeats, beatsPerBar, kit, drumNames, editable, scale, playheadBeats, onChange, onAudition }: Props) {
  const count = stepCount(clipBeats, stepBeats);
  /** Rows the player asked for beyond the clip's own (a melodic clip shows only
   * its pitches; "+" above or below opens the next semitone to program). */
  const [extraRows, setExtraRows] = useState<readonly number[]>([]);
  const rows = useMemo(() => {
    const base = rowsFor(notes, kit, scale, drumNames);
    const known = new Set(base.map((r) => r.pitch));
    for (const pitch of extraRows) if (!known.has(pitch)) base.push({ pitch, label: (kit ? drumLabel(pitch, drumNames) : null) ?? pitchLabel(pitch), ...(scale ? { tone: toneOf(pitch, scale) } : {}) });
    return base.sort((a, b) => b.pitch - a.pitch);
  }, [notes, kit, scale, drumNames, extraRows]);
  const rowPitches = useMemo(() => rows.map((r) => r.pitch), [rows]);
  const perBar = Math.max(1, Math.round(beatsPerBar / stepBeats));
  const playStep = playheadBeats === undefined ? -1 : stepOf(playheadBeats, stepBeats);
  const [sweep, setSweep] = useState<Sweep | null>(null);
  const sweepRef = useRef<Sweep | null>(null);
  const shown = sweep?.notes ?? notes;
  const tableRef = useRef<HTMLTableElement>(null);
  /** The cell a keyboard move landed on: focus follows the hit there once
   * the edit is back in `notes` (the button that had focus stays behind). */
  const [focusCell, setFocusCell] = useState<StepCell | null>(null);
  useEffect(() => {
    if (!focusCell) return;
    const el = tableRef.current?.querySelector<HTMLButtonElement>(`[data-pitch="${focusCell.pitch}"][data-step="${focusCell.step}"]`);
    if (el?.getAttribute("aria-pressed") !== "true") return; // the edit has not landed yet
    el.focus();
    setFocusCell(null);
  }, [focusCell, notes]);

  const audition = (pitch: number, velocity: number): void => {
    onAudition?.({ pitch, startTime: 0, duration: stepBeats, velocity });
  };

  /** The cell under a pointer, by the data the cells carry (touch never
   * fires enter/leave on the cells a finger crosses; a point lookup does). */
  const cellAt = (x: number, y: number): StepCell | null => {
    const el = document.elementFromPoint(x, y)?.closest<HTMLElement>("[data-step]");
    if (!el) return null;
    const pitch = Number(el.dataset["pitch"]);
    const step = Number(el.dataset["step"]);
    return Number.isFinite(pitch) && Number.isFinite(step) ? { pitch, step } : null;
  };

  const press = (e: React.PointerEvent<HTMLButtonElement>, pitch: number, step: number, lit: boolean, velocity: number): void => {
    if (!editable || sweepRef.current) return;
    if (e.button !== 0 && e.button !== 2) return;
    e.preventDefault();
    // ⇧: the hit's velocity cycles (an empty cell gains an accent). One
    // cell, one edit — no sweep.
    if (e.shiftKey && e.button === 0) {
      const v = lit ? nextVelocity(velocity) : 127;
      onChange([...(lit ? setStepVelocity(notes, pitch, step, stepBeats, v) : setStep(notes, pitch, step, stepBeats, true, v))]);
      audition(pitch, v);
      return;
    }
    const origin: StepCell = { pitch, step };
    // The right button erases; a lit cell under the left one is a hit to
    // move (or, if the press never leaves it, to clear); an empty one paints.
    const mode: Sweep["mode"] = e.button === 2 ? "erase" : lit ? "move" : "paint";
    const state: Sweep = {
      mode,
      from: origin,
      to: origin,
      notes: mode === "move" ? notes : setStep(notes, pitch, step, stepBeats, mode === "paint"),
      crossed: new Set([`${pitch}:${step}`]),
      pointerId: e.pointerId,
    };
    if (mode === "paint") audition(pitch, 100);
    sweepRef.current = state;
    setSweep(state);
    const move = (ev: PointerEvent): void => {
      if (ev.pointerId !== state.pointerId) return;
      const cell = cellAt(ev.clientX, ev.clientY);
      if (!cell) return;
      const key = `${cell.pitch}:${cell.step}`;
      if (state.mode === "move") {
        if (cell.pitch === state.to.pitch && cell.step === state.to.step) return;
        state.crossed.add(key);
        if (cell.pitch !== state.to.pitch) audition(cell.pitch, velocity);
        state.to = cell;
        state.notes = moveStep(notes, state.from, cell, stepBeats);
        setSweep({ ...state });
        return;
      }
      if (state.crossed.has(key)) return;
      state.crossed.add(key);
      const before = state.notes;
      state.notes = paintSteps(state.notes, [cell], stepBeats, state.mode === "paint");
      if (state.notes !== before && state.mode === "paint") audition(cell.pitch, 100);
      setSweep({ ...state });
    };
    const up = (ev: PointerEvent): void => {
      if (ev.pointerId !== state.pointerId) return;
      window.removeEventListener("pointermove", move);
      window.removeEventListener("pointerup", up);
      window.removeEventListener("pointercancel", up);
      sweepRef.current = null;
      setSweep(null);
      // A press on a hit that never left its cell was a tap: the hit clears.
      // (A cancelled one — the browser took the pointer — does nothing.)
      if (state.mode === "move" && state.crossed.size === 1) {
        if (ev.type === "pointerup") onChange([...setStep(notes, pitch, step, stepBeats, false)]);
        return;
      }
      if (state.notes === notes) return;
      // A moved hit keeps the keys' attention: arrows carry on from where it landed.
      if (state.mode === "move") setFocusCell(state.to);
      onChange([...state.notes]);
    };
    window.addEventListener("pointermove", move);
    window.addEventListener("pointerup", up);
    window.addEventListener("pointercancel", up);
  };

  /** A focused hit takes the roll's keys: Delete / Backspace clear it,
   * ← → move it a step (⇧: a bar), ↑ ↓ a row. Handled here, so the roll's
   * and the page's own bindings for the same keys stay out of it. */
  const keyOnHit = (e: React.KeyboardEvent<HTMLButtonElement>, cell: StepCell): void => {
    if (!editable || sweepRef.current || e.metaKey || e.ctrlKey || e.altKey) return;
    if (e.key === "Delete" || e.key === "Backspace") {
      e.preventDefault();
      e.stopPropagation();
      onChange([...setStep(notes, cell.pitch, cell.step, stepBeats, false)]);
      return;
    }
    const along = e.key === "ArrowLeft" ? -1 : e.key === "ArrowRight" ? 1 : 0;
    const across = e.key === "ArrowUp" ? -1 : e.key === "ArrowDown" ? 1 : 0;
    if (along === 0 && across === 0) return;
    e.preventDefault();
    e.stopPropagation();
    const to = nudgeCell(rowPitches, count, cell, across, along * (e.shiftKey ? perBar : 1));
    if (!to) return;
    const next = moveStep(notes, cell, to, stepBeats);
    if (next === notes) return;
    if (to.pitch !== cell.pitch) audition(to.pitch, 100);
    setFocusCell(to);
    onChange([...next]);
  };

  const addRow = (pitch: number): void => {
    setExtraRows((r) => (r.includes(pitch) ? r : [...r, pitch]));
  };
  // Rows run high to low: the next row above the top one, below the bottom one.
  const highest = rows[0]?.pitch;
  const lowest = rows[rows.length - 1]?.pitch;
  const above = highest === undefined ? null : neighbourPitch(highest, 1, kit ? undefined : scale);
  const below = lowest === undefined ? null : neighbourPitch(lowest, -1, kit ? undefined : scale);
  const rowTitle = (tone: "root" | "in" | "out" | undefined): string | undefined =>
    !scale || !tone ? undefined : tone === "root" ? `the root of ${scale.label}` : tone === "in" ? `in ${scale.label}` : `outside ${scale.label} — a note the clip holds`;

  if (count === 0) return null;
  return (
    <div
      className="overflow-x-auto p-2"
      data-testid="step-grid"
      style={{ touchAction: "none" }}
      // The right button erases (FL's habit) — the menu it would open stays shut.
      onContextMenu={(e) => e.preventDefault()}
    >
      <table ref={tableRef} className="border-separate border-spacing-0.5 text-[11px] select-none">
        <tbody>
          {editable && above !== null && (
            <tr>
              <th scope="row" className="pr-2 text-left font-normal">
                <RowAdder label={`Add a row above (${pitchLabel(above)})`} onClick={() => addRow(above)} />
              </th>
            </tr>
          )}
          {rows.map((row) => {
            const cells = stepsOf(shown, row.pitch, stepBeats, count);
            return (
              <tr key={row.pitch}>
                <th
                  scope="row"
                  title={rowTitle(row.tone) ?? (kit && row.label !== pitchLabel(row.pitch) ? `${row.label} · ${pitchLabel(row.pitch)}` : undefined)}
                  className={cn(
                    "max-w-[9rem] truncate whitespace-nowrap pr-2 text-left font-normal text-muted-foreground",
                    row.tone === "root" && "font-semibold text-foreground",
                    row.tone === "out" && "italic opacity-60",
                  )}
                >
                  {row.label}
                </th>
                {cells.map((cell, step) => {
                  const accent = cell.on && cell.velocity >= ACCENT;
                  const ghost = cell.on && cell.velocity <= GHOST;
                  const level = accent ? "accent" : ghost ? "ghost" : "normal";
                  const moving = sweep?.mode === "move" && sweep.to.pitch === row.pitch && sweep.to.step === step && sweep.crossed.size > 1;
                  return (
                    <td key={step} className={cn(step % perBar === 0 && step > 0 && "pl-1.5")}>
                      <button
                        type="button"
                        disabled={!editable}
                        data-pitch={row.pitch}
                        data-step={step}
                        aria-pressed={cell.on}
                        aria-label={`${row.label} step ${step + 1}${cell.on ? ` (${level}, velocity ${cell.velocity})` : ""}`}
                        title={
                          cell.on
                            ? `${level} hit, velocity ${cell.velocity} — tap to clear, drag to move, right-drag to clear a run, ⇧-tap to cycle accent / ghost / normal (focused: Delete clears, arrows move)`
                            : "tap for a hit, drag to paint a run, ⇧-tap for an accent"
                        }
                        onPointerDown={(e) => press(e, row.pitch, step, cell.on, cell.velocity)}
                        onKeyDown={cell.on ? (e) => keyOnHit(e, { pitch: row.pitch, step }) : undefined}
                        className={cn(
                          "relative h-7 w-7 rounded-sm border",
                          cell.on
                            ? "border-[var(--primary)] bg-[var(--primary)]"
                            : beatGroup(step, stepBeats) % 2 === 0
                              ? "border-border bg-muted/70 hover:bg-muted"
                              : "border-border bg-muted/30 hover:bg-muted",
                          !cell.on && row.tone === "out" && "opacity-50",
                          accent && "ring-2 ring-[var(--primary)]/60 ring-offset-1 ring-offset-background",
                          moving && "ring-2 ring-foreground/70 ring-offset-1 ring-offset-background",
                          step === playStep && "outline outline-1 outline-offset-1 outline-foreground/60",
                        )}
                        style={cell.on ? { opacity: ghost ? 0.45 : 1 } : undefined}
                      >
                        {ghost && <span aria-hidden className="absolute inset-[7px] rounded-[2px] bg-background/70" />}
                      </button>
                    </td>
                  );
                })}
              </tr>
            );
          })}
          {editable && below !== null && (
            <tr>
              <th scope="row" className="pr-2 text-left font-normal">
                <RowAdder label={`Add a row below (${pitchLabel(below)})`} onClick={() => addRow(below)} />
              </th>
            </tr>
          )}
        </tbody>
      </table>
    </div>
  );
}

function RowAdder({ label, onClick }: { label: string; onClick: () => void }) {
  return (
    <button
      type="button"
      aria-label={label}
      title={label}
      onClick={onClick}
      className="h-5 rounded-sm border border-dashed border-border px-1.5 text-[10px] text-muted-foreground hover:text-foreground"
    >
      +
    </button>
  );
}
